import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

/**
 * Generic Stripe success page used for both orders and tickets.
 * Reads ?session_id=... and polls /api/checkout/status/{session_id} until paid.
 */
export default function CheckoutSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { clearCart, t, formatPrice } = useApp();
  const [status, setStatus] = useState("loading"); // loading | paid | expired | error
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      setError("Missing session_id");
      return;
    }

    // Free-checkout fast-path — order was already marked paid by the server.
    if (sessionId.startsWith("FREE-")) {
      setStatus("paid");
      setData({
        payment_status: "paid",
        kind: "order",
        ref_id: params.get("order") || sessionId.replace("FREE-", ""),
        amount: 0,
        free: true,
      });
      clearCart();
      return;
    }

    let attempts = 0;
    const max = 8;
    let cancelled = false;

    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (data.kind === "order") clearCart();
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("expired");
          return;
        }
        if (attempts < max) {
          setTimeout(poll, 2000);
        } else {
          // We have a session but payment hasn't confirmed yet — show pending state
          setStatus("pending");
        }
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setError(e?.response?.data?.detail || "Failed to check status");
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  if (status === "loading") {
    return (
      <div className="max-w-[800px] mx-auto px-5 py-32 text-center" data-testid="checkout-loading">
        <Loader2 className="mx-auto animate-spin text-kult-pink" size={36} />
        <p className="font-mono text-xs tracking-[0.3em] text-kult-muted mt-6">CONFIRMING PAYMENT...</p>
      </div>
    );
  }

  if (status === "paid") {
    const isTicket = data?.kind === "ticket";
    return (
      <div className="max-w-[800px] mx-auto px-5 py-20 text-center" data-testid="checkout-paid">
        <CheckCircle2 size={48} className="mx-auto text-kult-yellow" />
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mt-4">⚡ PAYMENT CONFIRMED</p>
        <h1 className="font-display text-6xl md:text-8xl mt-3 leading-none">
          {isTicket ? "TICKETS LOCKED IN." : "ORDER PLACED."}
        </h1>
        <p className="text-kult-muted mt-4 text-lg">
          {isTicket
            ? "Your tickets have been issued. Show this code at the door."
            : "Your order has been received. We'll be in touch."}
        </p>
        <div className="mt-8 border-2 border-kult-pink p-6 inline-block kult-shadow-yellow">
          <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mb-2">
            {isTicket ? "TICKET CODE" : "ORDER NUMBER"}
          </p>
          <p className="font-display text-3xl break-all">{data?.ref_id}</p>
          {data?.amount && (
            <p className="font-mono text-xs text-kult-muted mt-2">{formatPrice(data.amount)} paid</p>
          )}
        </div>
        <div className="mt-10 flex justify-center gap-4 flex-wrap">
          <button onClick={() => navigate("/")} className="bg-kult-pink text-black px-8 py-4 font-display text-xl">
            BACK TO HOME
          </button>
          <button onClick={() => navigate(isTicket ? "/events" : "/shop")} className="border-2 border-white px-8 py-4 font-display text-xl">
            {isTicket ? "ALL EVENTS" : "KEEP SHOPPING"}
          </button>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    const isTicket = data?.kind === "ticket";
    return (
      <div className="max-w-[800px] mx-auto px-5 py-20 text-center" data-testid="checkout-pending">
        <Loader2 className="mx-auto animate-spin text-kult-yellow" size={36} />
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow mt-4">⚡ PAYMENT PROCESSING</p>
        <h1 className="font-display text-5xl mt-3 leading-none">ALMOST THERE.</h1>
        <p className="text-kult-muted mt-4">
          {isTicket
            ? "Your tickets are being confirmed. You'll get a confirmation email shortly."
            : "Your order is being processed. You'll get a confirmation email shortly."}
        </p>
        {data?.ref_id && (
          <p className="font-mono text-xs text-kult-muted mt-3">REF · {data.ref_id}</p>
        )}
        <button onClick={() => navigate("/")} className="mt-8 bg-kult-pink text-black px-8 py-4 font-display text-xl">
          BACK TO HOME
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-[800px] mx-auto px-5 py-20 text-center" data-testid="checkout-error">
      <XCircle size={48} className="mx-auto text-kult-pink" />
      <h1 className="font-display text-5xl mt-4 leading-none">PAYMENT FAILED.</h1>
      <p className="text-kult-muted mt-4">
        {error || "We couldn't confirm your payment. Please try again."}
      </p>
      <button onClick={() => navigate(-1)} className="mt-8 bg-kult-pink text-black px-8 py-4 font-display text-xl">
        TRY AGAIN
      </button>
    </div>
  );
}